import React, { useEffect, useState, useRef } from 'react';
import { FormattedMessage} from "react-intl";
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CostoFijoTooltip from "./CostoFijoTooltip";
import { limitMontoDigits, usePriceInput } from '../ui/commons/inputs';
import { formatCost } from '../../commons/utils/Helpers';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { ITEM_TIPO_MANO_DE_OBRA } from '../../commons/constants/itemsTipos.js';
import { CANTIDAD_STEP, recortarCantidad, bloquearTeclaCantidad } from './cantidadDecimal';
import swal from 'sweetalert';

const PresupuestoDetalladoEditModal = (props) => {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState({cantidad: 0, costo: 0, detalle: '', clasificacion: '', id_item_clasificacion: null});

	useEffect(() => {
		init();
	}, []);

	const init = () => {
		let copy = JSON.parse(JSON.stringify(data));
        copy.cantidad = props.element.cantidad;
		copy.detalle = props.element.detalle || '';
		copy.clasificacion = props.element.clasificacion || '';
		// VEC-3278: el presupuesto detallado usa 'id_item_clasificacion'. Las filas del cierre (modal compartido)
		// traen las dos claves: 'id_clasificacion' es la vigente (se actualiza al editar) y 'id_item_clasificacion'
		// es la columna de DB, que queda con el valor original. Por eso, si la fila trae 'id_clasificacion', esa clave
		// manda aunque sea null (clasificación borrada): mismo criterio que resolverIdItemClasificacion en el backend.
		var clasificacionFila = 'id_clasificacion' in props.element ? props.element.id_clasificacion : props.element.id_item_clasificacion;
		copy.id_item_clasificacion = clasificacionFila || null;
		switch (props.type) {
			case "presupuestoTarea":
				copy.costo = formatCost(props.element.precio);
				break;
			case "presupuestoItem":
				copy.costo = formatCost(props.element.costo);
				break;
			default:
				console.log('default');
		}
		setData(copy);
	};

	const handleMaterialChange = (name, value) => {
		let copy = JSON.parse(JSON.stringify(data));
		if (name === 'detalle') {
			if (value.length <= 60) {
				copy[name] = value;
			}
		} else {
			if (name === 'costo') {
				const limited = limitMontoDigits(value);
				if ((limited && limited >= 0) || !limited) {
					copy[name] = limited;
				}
			} else if ((value && value >= 0) || !value) {
				copy[name] = value;
			}
		}
		setData(copy);
	}

	const obligatoriosConfigKey = props.type === 'presupuestoTarea'
		? 'tickets.presupuesto.tareas.obligatorios'
		: 'tickets.presupuesto.items.obligatorios';
	const obligatoriosConfig = ConfigBusiness.get(obligatoriosConfigKey) || '';
	const obligatoriosArr = obligatoriosConfig.split(',').map(function(s) { return s.trim(); });
	const clasificacionObligatorio = obligatoriosArr.indexOf('clasificacion') !== -1;
	const costoObligatorio = obligatoriosArr.indexOf('costo') !== -1 || obligatoriosArr.indexOf('precio') !== -1;
	const cantidadObligatorio = obligatoriosArr.indexOf('cantidad') !== -1;

	const handleGuardar = () => {
		// VEC-2591: si la cantidad es obligatoria, no permitir guardar en 0 ni vacía (0.1 sí es válido).
		const cantidadFaltante = data.cantidad === '' || data.cantidad === null || data.cantidad === undefined || parseFloat(data.cantidad) === 0;
		if (cantidadObligatorio && props.enabledCantidad && cantidadFaltante) {
			swal({
				title: props.intl.formatMessage({ id: 'presupuesto.cantidad_requerida', defaultMessage: 'La cantidad no puede ser 0' }),
				icon: 'warning',
				button: { className: 'btn btn-primary' }
			});
			return;
		}
		if (clasificacionObligatorio && props.enabledClasificacion && !data.id_item_clasificacion) {
			swal({
				title: props.intl.formatMessage({ id: 'presupuesto.clasificacion_requerida', defaultMessage: 'La clasificación es obligatoria' }),
				icon: 'warning',
				button: { className: 'btn btn-primary' }
			});
			return;
		}
		// VEC-3436: si el costo/precio es obligatorio, no permitir guardar vacío desde el modal.
		// El 0 es un valor válido; solo se bloquea el campo vacío (null/undefined/'').
		const costoVacio = data.costo === undefined || data.costo === null || data.costo === '';
		if (costoObligatorio && props.enabledCosto && costoVacio) {
			swal({
				title: props.intl.formatMessage({ id: 'presupuesto.costo_requerido', defaultMessage: 'El precio no puede quedar vacío (puede ser 0)' }),
				icon: 'warning',
				button: { className: 'btn btn-primary' }
			});
			return;
		}
		props.updateElement(data);
	};

	// Calcular cuántos campos están activos en la tabla (sin detalle, que va aparte)
	const camposActivosTabla = [props.enabledCantidad, props.enabledCosto].filter(Boolean).length;

	// Determinar el ancho máximo según la cantidad de campos activos en la tabla
	const getMaxWidth = () => {
		if (camposActivosTabla === 1) return '300px';
		return '500px';
	};

	// VEC-3556: la cantidad admite decimales en todo el presupuesto detallado: Mano de Obra (como ítem o
	// como tarea) y Repuestos/Producto. Antes solo MO como ítem lo permitía (VEC-3532).
	// VEC-3570: el tope pasó de 1 a 2 decimales; la regla vive en cantidadDecimal.js.

	const costoInput = usePriceInput(data.costo, (value) => handleMaterialChange('costo', value));

	return (
		<>
			<div>
				<div className="text-center mt-2">
					<h3>
						{
							props.element.id_tarea || (props.element.id_item && ConfigBusiness.get('tickets.trabajaConManoDeObra.habilitado') === 'true' && props.element.tipo && props.element.tipo.toLowerCase() === ITEM_TIPO_MANO_DE_OBRA.toLowerCase()) ?
								<FormattedMessage id="presupuesto.mano_de_obra" defaultMessage="Mano de Obra" />
							: props.element.id_item ?
								<FormattedMessage id="presupuesto.repuesto" defaultMessage="Repuesto" />
							: <FormattedMessage id="presupuesto.error_nombre" defaultMessage="No se encontró el nombre del repuesto o tarea" />
						}
						:&nbsp;
						{props.element.descripcion ? (props.element.descripcion) : (props.element.nombre ? props.element.nombre : '')}
					</h3>
				</div>
				<div className="table-responsive" style={{padding: "15px", display: 'flex', justifyContent: 'center'}}>
					<table
						id="dataTable"
						className="dataTable table nowrap server-side table-hover dataTableMovil"
						style={{maxWidth: getMaxWidth(), width: '100%'}}
					>
						<thead>
							<tr>
								{
									props.enabledCantidad ?
										<th scope="col" style={{textAlign: 'center'}}>
											<FormattedMessage id="presupuesto.cantidad" defaultMessage="Cantidad" />
										</th>
									: null
								}
                                {
									props.enabledCosto ?
                                        <th scope="col" style={{textAlign: 'center'}}>
											<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                            	<FormattedMessage id="Ppresupuesto.precio" defaultMessage="Precio" />
												<CostoFijoTooltip
													costoFijo={props.element.costo_fijo === 1}
													descripcion={props.element.descripcion || props.element.nombre}
												/>
											</span>
                                        </th>
									: null
								}
							</tr>
						</thead>
						<tbody>
							<tr key={props.element.id}>
								{
									props.enabledCantidad ?
										<td className="all custom-lineHeight" style={{textAlign: 'center'}}>
											<TextField
												id="number"
												type="text"
												onChange={e => handleMaterialChange('cantidad', recortarCantidad(e.target.value))}
												onKeyDown={bloquearTeclaCantidad}
												InputProps={{
													inputProps: { min: 0, inputMode: 'decimal', step: CANTIDAD_STEP, 'data-outline-none': 'true', style: { textAlign: 'center' } }
												}}
												variant="standard"
												value={data.cantidad}
												fullWidth
											/>
										</td>
									: null
								}
								{
									props.enabledCosto ?
										<td className="all custom-lineHeight" style={{textAlign: 'center'}}>
											<TextField
												id="standard"
												{...costoInput}
												disabled={props.element.costo_fijo == 1}
												InputProps={{
													inputProps: {
														min: 0,
														'data-outline-none': 'true',
														style: { textAlign: 'center' }
													}
												}}
												variant="standard"
												fullWidth
											/>
										</td>
									: null
								}
							</tr>
						</tbody>
					</table>
				</div>
				{
					props.enabledClasificacion ?
						<div style={{padding: '0 15px 15px 15px'}}>
							<Autocomplete
								id="clasificacion-edit"
								name="clasificacion-edit"
								options={props.clasificaciones || []}
								value={(props.clasificaciones || []).find(function(c) { return c.id === data.id_item_clasificacion; }) || null}
								getOptionLabel={function(option) { return option.nombre ? option.nombre : ''; }}
								onChange={function(event, value) {
									var copy = JSON.parse(JSON.stringify(data));
									if (value) {
										copy.clasificacion = value.nombre;
										copy.id_item_clasificacion = value.id;
									} else {
										copy.clasificacion = '';
										copy.id_item_clasificacion = null;
									}
									setData(copy);
								}}
								renderInput={function(params) {
									return (
										<TextField
											{...params}
											label={props.intl.formatMessage({ id: 'Presupuesto.Clasificación', defaultMessage: 'Clasificación' })}
											placeholder={props.intl.formatMessage({ id: 'Presupuesto.Clasificación', defaultMessage: 'Clasificación' })}
											variant="outlined"
										/>
									);
								}}
							/>
						</div>
					: null
				}
				{
					props.enabledDetalle ?
						<div style={{padding: '0 15px 15px 15px'}}>
							<label style={{fontWeight: 'bold', marginBottom: '4px', display: 'block'}}>
								<FormattedMessage id="presupuesto.detalle" defaultMessage="Detalle" />
							</label>
							<TextField
								id="detalle"
								type="text"
								multiline
								rows={2}
								onChange={e => handleMaterialChange('detalle', e.target.value)}
								inputProps={{
									'data-outline-none': 'true',
									maxLength: 60
								}}
								variant="outlined"
								value={data.detalle}
								fullWidth
							/>
							<div style={{textAlign: 'right', fontSize: '0.75rem', color: '#666', marginTop: '2px'}}>
								{data.detalle ? data.detalle.length : 0}/60
							</div>
						</div>
					: null
				}
				<div className="text-center d-flex justify-content-center">
					<button type="button" className="btn btn-primary mr-1" onClick={handleGuardar}>
						<i className="fa fa-check-circle"></i><FormattedMessage id="presupuesto.guardar" defaultMessage=" Guardar"/>
					</button>
					<button type="button" className="btn btn-danger" onClick={(e) => props.closeModalEdit()}>
						<i className="fa fa-times-circle"></i> { props.intl.formatMessage({ id: 'presupuesto.cancelar', defaultMessage: 'Cancelar' })}
					</button>
				</div>
			</div>
		</>
	);
};

export default PresupuestoDetalladoEditModal;
